import { formatShortDateWithWeekday } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ExternalAccountDetailsAttributes } from "@src/appV2/redesign/Accounts/PayoutSettings/api/schema";
import { Card, Text } from "@src/appV2/redesign/system/ui-components";
import { isPast, parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";

import { type ShiftPaymentStatus } from "../../api/useGetShiftPaymentStatus";
import { type SupportShiftDetails } from "../../api/useShiftDetails";
import { type ScoreLedgerEntry } from "../../api/useShiftScoreLedger";
import { useScoreImpact } from "../hooks/useScoreImpact";
import { formatTimeToShift } from "../utils/formatTimeToShift";
import { ClipboardScoreCard } from "./ClipboardScoreCard";
import { EarningsCard } from "./EarningsCard";
import { ScoreImpactSection } from "./ScoreImpactSection";
import { ShiftInfoRow } from "./ShiftInfoRow";
import { TimekeepingCard } from "./TimekeepingCard";

interface ShiftDetailsContentProps {
  readonly shiftDetails: SupportShiftDetails;
  readonly scoreLedger: ScoreLedgerEntry[];
  readonly isScoreLedgerLoading: boolean;
  readonly isPastShift: boolean;
  readonly payoutDestination: ExternalAccountDetailsAttributes | undefined;
  readonly paymentStatus: ShiftPaymentStatus | undefined;
  readonly isPaymentStatusLoading: boolean;
}

export function ShiftDetailsContent(props: ShiftDetailsContentProps) {
  const {
    shiftDetails,
    scoreLedger,
    isScoreLedgerLoading,
    isPastShift,
    payoutDestination,
    paymentStatus,
    isPaymentStatusLoading,
  } = props;
  const { workplaceTimezone } = shiftDetails;

  const startDate = parseISO(shiftDetails.startAt);
  const formattedDate = formatShortDateWithWeekday(startDate, { timeZone: workplaceTimezone });
  const scheduledTimeRange = `${formatTime(shiftDetails.startAt, workplaceTimezone)} - ${formatTime(
    shiftDetails.endAt,
    workplaceTimezone
  )}`;
  const hasClockData = isDefined(shiftDetails.clockInTime) || isDefined(shiftDetails.clockOutTime);
  const totalScoreChange = scoreLedger.reduce((sum, entry) => sum + entry.adjustment, 0);

  const scoreImpactData = useScoreImpact({
    shiftId: shiftDetails.id,
    shiftStartIso: shiftDetails.startAt,
    qualification: shiftDetails.qualificationName,
    workplaceId: shiftDetails.workplaceId,
    targetArrivalTime: shiftDetails.targetArrivalTime,
    bookedAt: shiftDetails.bookedAt,
    enabled: !(isPastShift || isDefined(shiftDetails.clockInTime)),
  });

  return (
    <Stack spacing={4}>
      <HeroCard
        formattedDate={formattedDate}
        qualificationName={shiftDetails.qualificationName}
        workplaceName={shiftDetails.workplaceName}
      />

      <ScheduledShiftCard
        scheduledTimeRange={scheduledTimeRange}
        workplaceAddress={shiftDetails.workplaceAddress}
        timeToShift={!isPastShift ? formatTimeToShift(startDate, new Date()) : undefined}
        requiredArrival={
          !isPastShift && isDefined(shiftDetails.targetArrivalTime)
            ? formatTime(shiftDetails.targetArrivalTime, workplaceTimezone)
            : undefined
        }
        estimatedArrival={
          !isPastShift && isDefined(scoreImpactData.etaTime)
            ? formatTime(scoreImpactData.etaTime.toISOString(), workplaceTimezone)
            : undefined
        }
      />

      {isPastShift && (
        <TimekeepingCard
          shiftDetails={shiftDetails}
          workplaceTimezone={workplaceTimezone}
          isVerified={shiftDetails.isVerified}
        />
      )}

      {isPastShift && hasClockData && shiftDetails.isVerified && (
        <EarningsCard
          grossEarningsCents={shiftDetails.grossEarningsCents}
          hourlyRateCents={shiftDetails.hourlyRateCents}
          actualDurationHours={shiftDetails.actualDurationHours}
          isInstantPay={shiftDetails.isInstantPay}
          isVerified={shiftDetails.isVerified}
          payoutDestination={payoutDestination}
          paymentStatus={paymentStatus}
          isPaymentStatusLoading={isPaymentStatusLoading}
        />
      )}

      {isPast(parseISO(shiftDetails.endAt)) && (
        <ClipboardScoreCard
          isLoading={isScoreLedgerLoading}
          scoreLedger={scoreLedger}
          totalScoreChange={totalScoreChange}
        />
      )}

      {!(isPastShift || isDefined(shiftDetails.clockInTime)) && (
        <ScoreImpactSection data={scoreImpactData} />
      )}

      {!(isPastShift || isDefined(shiftDetails.clockInTime)) &&
        scoreImpactData.isShiftApproaching && (
          <Card variant="tertiary" sx={(theme) => ({ padding: theme.spacing(5) })}>
            <Stack spacing={3}>
              <Text semibold variant="body1">
                Things to keep in mind
              </Text>
              <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                {scoreImpactData.shouldShowEtaImpact && isDefined(scoreImpactData.etaTime)
                  ? "Score impact updates as your ETA changes."
                  : "ETA not available. Please ensure location permissions are granted so we can track your arrival time."}
              </Text>
              <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                Not showing up without canceling leads to immediate account restriction.
              </Text>
            </Stack>
          </Card>
        )}
    </Stack>
  );
}

interface HeroCardProps {
  readonly workplaceName: string;
  readonly formattedDate: string;
  readonly qualificationName: string | undefined;
}

function HeroCard({ workplaceName, formattedDate, qualificationName }: HeroCardProps) {
  const subtitle = isDefined(qualificationName)
    ? `${formattedDate} · ${qualificationName}`
    : formattedDate;

  return (
    <Card variant="quaternary" sx={(theme) => ({ padding: theme.spacing(5) })}>
      <Stack spacing={1} alignItems="center">
        <Text semibold variant="h5" sx={{ textAlign: "center" }}>
          {workplaceName}
        </Text>
        <Text
          variant="body2"
          sx={(theme) => ({ color: theme.palette.text.secondary, textAlign: "center" })}
        >
          {subtitle}
        </Text>
      </Stack>
    </Card>
  );
}

interface ScheduledShiftCardProps {
  readonly scheduledTimeRange: string;
  readonly workplaceAddress: string | undefined;
  readonly timeToShift: string | undefined;
  readonly requiredArrival: string | undefined;
  readonly estimatedArrival: string | undefined;
}

function ScheduledShiftCard(props: ScheduledShiftCardProps) {
  const { scheduledTimeRange, workplaceAddress, timeToShift, requiredArrival, estimatedArrival } =
    props;

  return (
    <Card variant="tertiary" sx={(theme) => ({ padding: theme.spacing(5) })}>
      <Stack spacing={3}>
        <Text semibold variant="body1">
          Scheduled Shift
        </Text>
        <ShiftInfoRow label="Time" value={scheduledTimeRange} />
        {isDefined(workplaceAddress) && <ShiftInfoRow label="Location" value={workplaceAddress} />}
        {isDefined(timeToShift) && <ShiftInfoRow label="Time to shift" value={timeToShift} />}
        {isDefined(requiredArrival) && (
          <ShiftInfoRow label="Required arrival" value={requiredArrival} />
        )}
        {isDefined(estimatedArrival) && (
          <ShiftInfoRow label="Estimated arrival" value={estimatedArrival} />
        )}
      </Stack>
    </Card>
  );
}

function formatTime(isoString: string, timezone: string): string {
  return formatInTimeZone(parseISO(isoString), timezone, "h:mm a");
}
